import { useState } from 'react';
import { Box, Collapse, IconButton, Typography, Stack, Chip } from '@mui/material';
import { FaChevronRight, FaChevronDown } from 'react-icons/fa';

const PRIMARY = '#2065D1';
const PRIMARY_DARK = '#103996';

export default function CourseStructureTree({ data }) {
  if (!data || data.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No structure to display yet. Select a course and subject, then click Search.
      </Typography>
    );
  }

  return (
    <Box>
      {data.map((course) => (
        <CourseNode key={course.parentId} node={course} level={0} />
      ))}
    </Box>
  );
}

function CourseNode({ node, level }) {
  const [isOpen, setIsOpen] = useState(level === 0);
  const hasChildren = node.children?.length > 0;
  const hasContent = node.content?.length > 0;
  const canExpand = hasChildren || hasContent;

  const toggleOpen = () => canExpand && setIsOpen((prev) => !prev);

  return (
    <Box
      sx={{
        borderLeft: level > 0 ? '2px solid' : 'none',
        borderColor: 'divider',
        ml: level > 0 ? 1.25 : 0,
        pl: level > 0 ? 2 : 0,
      }}
    >
      <Box
        onClick={toggleOpen}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          py: 0.75,
          cursor: canExpand ? 'pointer' : 'default',
          borderRadius: 1,
          '&:hover': canExpand ? { bgcolor: 'action.hover' } : undefined,
        }}
      >
        {canExpand ? (
          <IconButton size="small" sx={{ p: 0.25 }} onClick={toggleOpen}>
            {isOpen ? <FaChevronDown size={12} color={PRIMARY} /> : <FaChevronRight size={12} color={PRIMARY} />}
          </IconButton>
        ) : (
          <Box sx={{ width: 24, flexShrink: 0 }} />
        )}
        <Typography
          variant={level === 0 ? 'subtitle1' : 'body1'}
          sx={{ fontWeight: level === 0 ? 700 : 600, color: 'text.primary' }}
        >
          {node.name}
        </Typography>
      </Box>

      <Collapse in={isOpen} unmountOnExit>
        <Box sx={{ pl: 4, pb: 1 }}>
          {hasContent && (
            <Stack direction="row" flexWrap="wrap" gap={1} sx={{ my: 1 }}>
              {node.content.map((item, idx) => {
                const isStream = item.fileUrl?.endsWith('.m3u8');
                const chipSx = {
                  bgcolor: PRIMARY,
                  color: '#fff',
                  fontWeight: 500,
                  '&:hover': { bgcolor: PRIMARY_DARK },
                };

                if (isStream) {
                  return (
                    <Chip
                      key={idx}
                      label={item.contentName}
                      clickable
                      sx={chipSx}
                      onClick={() => {
                        const encodedUrl = encodeURIComponent(item.fileUrl);
                        window.open(`/dashboard/m3u8-player.html?url=${encodedUrl}`, '_blank');
                      }}
                    />
                  );
                }

                return (
                  <Chip
                    key={idx}
                    label={item.contentName}
                    component="a"
                    href={item.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    clickable
                    sx={chipSx}
                  />
                );
              })}
            </Stack>
          )}

          {hasChildren &&
            node.children.map((child) => <CourseNode key={child.parentId} node={child} level={level + 1} />)}
        </Box>
      </Collapse>
    </Box>
  );
}
